import { useEffect, useState } from "react";
import { ArrowUpRight, Crown, MapPin, Search } from "lucide-react";
import { api, buildCarPath } from "@/lib/api";
import { useAppTranslation } from "@/i18n/useAppTranslation";

// Verified against the active public listing: owner 9271, display_order 0.
// This is a marketing illustration, never a mutation of the actual car card.
export const BEACH_GYPSYS_EXAMPLE = {
  id: 9746,
  ownerId: "9271",
  slug: "1957-porsche-speedster-9746",
  countryCode: "US",
  location: "Huntington Beach, CA",
  image: "https://pub-b0c8d82c3e244a2fbf1133bf122e76fa.r2.dev/cars/9746/legacy-53998_800w.webp",
  imageFallback: "https://pub-b0c8d82c3e244a2fbf1133bf122e76fa.r2.dev/cars/9746/legacy-53998_2400w.webp",
} as const;

export function ProOwnerListingPreview() {
  const { t } = useAppTranslation();
  const [listingAvailable, setListingAvailable] = useState(false);
  const [listingPath, setListingPath] = useState("");
  const [imageFallback, setImageFallback] = useState(0);
  const [imageCandidates, setImageCandidates] = useState<string[]>([
    BEACH_GYPSYS_EXAMPLE.image,
    BEACH_GYPSYS_EXAMPLE.imageFallback,
  ]);

  useEffect(() => {
    let cancelled = false;
    // Full public detail includes car.owner_id and display_order on top-level
    // images. The lightweight ID-only endpoint does not provide either.
    api.getCarDetail(BEACH_GYPSYS_EXAMPLE.id, BEACH_GYPSYS_EXAMPLE.slug)
      .then(({ car, images }) => {
        const hero = images?.find((image) => image.display_order === 0);
        if (!cancelled) {
          const verified =
            Number(car.id) === BEACH_GYPSYS_EXAMPLE.id &&
            String(car.owner_id) === BEACH_GYPSYS_EXAMPLE.ownerId &&
            car.slug === BEACH_GYPSYS_EXAMPLE.slug &&
            Boolean(hero);
          const currentHero = hero?.cdn_medium || hero?.url;
          if (verified && currentHero) {
            setImageCandidates([...new Set([currentHero, BEACH_GYPSYS_EXAMPLE.image, BEACH_GYPSYS_EXAMPLE.imageFallback])]);
            setImageFallback(0);
          }
          setListingAvailable(verified);
          setListingPath(verified ? buildCarPath(car) : "");
        }
      })
      .catch(() => {
        if (!cancelled) setListingAvailable(false);
      });
    return () => { cancelled = true; };
  }, []);

  return (
    <section id="preview" className="scroll-mt-20 bg-[#f3f0e8] md:scroll-mt-24 py-16 sm:py-24" aria-labelledby="pro-preview-title">
      <div className="mx-auto grid max-w-6xl items-center gap-10 px-4 lg:grid-cols-[minmax(0,1fr)_minmax(0,1.1fr)] lg:gap-16">
        <div>
          <p className="text-xs font-bold uppercase tracking-[0.22em] text-brand">{t("professional.proOwner.preview_eyebrow")}</p>
          <h2 id="pro-preview-title" className="mt-4 max-w-lg font-serif text-4xl leading-[1.08] text-brand sm:text-5xl">
            {t("professional.proOwner.preview_title")}
          </h2>
          <p className="mt-6 max-w-lg text-lg leading-relaxed text-foreground/80">
            {t("professional.proOwner.preview_intro")}
          </p>
          <div className="mt-8 space-y-6 border-l-2 border-amber-500 pl-6">
            <div>
              <div className="flex items-center gap-2 font-semibold text-brand"><Crown className="h-5 w-5" />{t("professional.proOwner.preview_badge_title")}</div>
              <p className="mt-1 text-sm leading-relaxed text-foreground/75">{t("professional.proOwner.preview_badge_body")}</p>
            </div>
            <div>
              <div className="flex items-center gap-2 font-semibold text-brand"><Search className="h-5 w-5" />{t("professional.proOwner.preview_search_title")}</div>
              <p className="mt-1 text-sm leading-relaxed text-foreground/75">{t("professional.proOwner.preview_search_body")}</p>
            </div>
          </div>
          <a href="#pricing" data-testid="link-preview-plans" className="mt-9 inline-flex min-h-11 w-full justify-center sm:w-auto items-center gap-2 rounded-lg bg-brand px-6 py-3 font-semibold text-white transition hover:bg-brand-light focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-4 focus-visible:outline-brand">
            {t("professional.proOwner.view_plans")} <ArrowUpRight className="h-4 w-4" />
          </a>
        </div>
        <div className="relative">
          <div className="absolute -inset-3 rounded-[2rem] border border-brand/15 sm:-inset-5" aria-hidden="true" />
          <div className="relative overflow-hidden rounded-[1.5rem] bg-white shadow-2xl shadow-brand/15">
            <div className="relative aspect-[4/3] bg-brand/10">
              {imageFallback < imageCandidates.length ? (
                <img
                  src={imageCandidates[imageFallback]}
                  onError={() => setImageFallback((current) => current + 1)}
                  alt={t("professional.proOwner.preview_image_alt")}
                  loading="lazy"
                  decoding="async"
                  className="h-full w-full object-cover"
                  data-testid="img-preview-car"
                />
              ) : (
                <div role="img" aria-label={t("professional.proOwner.preview_image_unavailable")} className="flex h-full items-center justify-center bg-brand text-center text-sm font-medium text-white">
                  {t("professional.proOwner.preview_image_unavailable")}
                </div>
              )}
              <div className="absolute left-4 top-4 right-4 flex flex-wrap items-start justify-between gap-2">
                <span className="rounded-full bg-brand px-3 py-1.5 text-[11px] font-bold uppercase tracking-wider text-white shadow-md">
                  {t("professional.proOwner.preview_label")}
                </span>
                <span className="inline-flex items-center gap-1 rounded-full bg-amber-400 px-3 py-1.5 text-[11px] font-bold uppercase tracking-wide text-brand shadow-md" data-testid="badge-preview-pro-owner">
                  <Crown className="h-3 w-3" /> {t("carCard.pro_owner")}
                </span>
              </div>
            </div>
            <div className="p-5 sm:p-7">
              <p className="text-xs font-bold uppercase tracking-[0.17em] text-brand/70">{t("professional.proOwner.preview_brand")} · {t("professional.proOwner.preview_example")}</p>
              <h3 className="mt-2 font-serif text-2xl text-brand" data-testid="text-preview-car-title">{t("professional.proOwner.preview_car_title")}</h3>
              <p className="mt-2 flex items-center gap-1.5 text-sm text-foreground/70"><MapPin className="h-4 w-4" />{BEACH_GYPSYS_EXAMPLE.location}</p>
              <p className="mt-5 border-t border-border pt-4 text-xs leading-relaxed text-foreground/70">{t("professional.proOwner.preview_disclaimer")}</p>
              {listingAvailable && listingPath && (
                <a href={`${import.meta.env.BASE_URL}${listingPath.replace(/^\//, "")}`} target="_blank" rel="noopener noreferrer" data-testid="link-preview-listing" className="mt-3 inline-flex min-h-11 items-center gap-1 font-semibold text-brand underline underline-offset-4 hover:text-brand-light focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-brand">
                  {t("professional.proOwner.preview_view_listing")} <ArrowUpRight className="h-4 w-4" />
                </a>
              )}
              {listingAvailable && !listingPath && (
                <p role="status" className="mt-3 text-xs text-muted-foreground">
                  {t("carCard.link_unavailable", { defaultValue: "Listing link temporarily unavailable" })}
                </p>
              )}
            </div>
          </div>
        </div>
      </div>
    </section>
  );
}